import { PageContext } from 'src/redux/feedback/feedback.types';

const SNAPSHOT_SELECTORS = [
  'h1',
  'h2',
  'h3',
  '[data-testid]',
  '[data-page]',
  '[data-route]',
  '[data-survey-id]',
  '[data-client-id]',
  'main h1',
  'main h2',
  'main h3',
  '[role="main"] h1',
  '[role="main"] h2',
].join(', ');
const FALLBACK_SNAPSHOT_SELECTORS = [
  'main',
  '[role="main"]',
  'nav a',
  'button',
  'label',
  '[aria-label]',
].join(', ');
const MAX_SNAPSHOT_ELEMENTS = 20;
const MAX_TEXT_LENGTH = 200;

function getActiveTabLabel(): string | undefined {
  // Supports our custom Tabs (`role="tab" aria-selected`) and some common libs.
  const activeTab = document.querySelector(
    [
      '[role="tab"][aria-selected="true"]',
      '[role="tab"][data-state="active"]',
      '.p-tabview-nav li.p-highlight',
    ].join(', ')
  );

  if (!activeTab) {
    return undefined;
  }

  const label = activeTab.textContent?.trim();
  return label ? label.slice(0, 80) : undefined;
}

function getPrimaryHeading(): string | undefined {
  const heading = document.querySelector('h1');
  const text = heading?.textContent?.trim();
  return text ? text.slice(0, 80) : undefined;
}

function isElementInViewport(element: Element): boolean {
  const rect = element.getBoundingClientRect();

  return rect.top < window.innerHeight && rect.bottom > 0 && rect.width > 0 && rect.height > 0;
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function appendSnapshotElement(elements: string[], element: Element): void {
  if (!isElementInViewport(element) || elements.length >= MAX_SNAPSHOT_ELEMENTS) {
    return;
  }

  const tagName = element.tagName.toLowerCase();
  const attributes = Array.from(element.attributes)
    .map((attribute) => `${attribute.name}="${escapeHtml(attribute.value)}"`)
    .join(' ');
  const text = escapeHtml(element.textContent?.trim().slice(0, MAX_TEXT_LENGTH) ?? '');

  if (!text && tagName !== 'div' && tagName !== 'main') {
    return;
  }

  elements.push(`<${tagName}${attributes ? ` ${attributes}` : ''}>${text}</${tagName}>`);
}

function captureDomSnapshotFromSelectors(selectors: string): string {
  const elements: string[] = [];

  document.querySelectorAll(selectors).forEach((element) => {
    appendSnapshotElement(elements, element);
  });

  return elements.join('\n');
}

function captureDomSnapshotFallback(): string {
  const snapshot = captureDomSnapshotFromSelectors(FALLBACK_SNAPSHOT_SELECTORS);
  if (snapshot.trim()) {
    return snapshot;
  }

  const path = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  const activeTab = getActiveTabLabel();
  const heading = getPrimaryHeading();
  const parts = [
    `<meta path="${escapeHtml(path)}">`,
    heading ? `<h1>${escapeHtml(heading)}</h1>` : '',
    activeTab ? `<tab active="${escapeHtml(activeTab)}">` : '',
  ].filter(Boolean);

  return parts.join('\n');
}

export function captureDomSnapshot(): string {
  const primary = captureDomSnapshotFromSelectors(SNAPSHOT_SELECTORS);
  if (primary.trim()) {
    return primary;
  }

  return captureDomSnapshotFallback();
}

export function capturePageContext(): PageContext {
  return {
    url: window.location.href,
    pageTitle: document.title,
    domSnapshot: captureDomSnapshot(),
  };
}

export function buildFeedbackTitle(): string {
  const parts: string[] = [];

  const heading = getPrimaryHeading();
  const activeTab = getActiveTabLabel();

  if (heading) {
    parts.push(heading);
  } else if (document.title) {
    parts.push(document.title);
  }

  if (activeTab && !parts.includes(activeTab)) {
    parts.push(activeTab);
  }

  const path = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  parts.push(path);

  return parts.join(' · ');
}

function getScreenshotCaptureTarget(): HTMLElement {
  return (
    document.querySelector<HTMLElement>('main')
    ?? document.querySelector<HTMLElement>('[role="main"]')
    ?? document.querySelector<HTMLElement>('#root')
    ?? document.body
  );
}

export async function captureScreenshotFile(): Promise<File | undefined> {
  try {
    const { default: html2canvas } = await import('html2canvas');
    const target = getScreenshotCaptureTarget();
    const canvas = await html2canvas(target, {
      backgroundColor: '#ffffff',
      useCORS: true,
      allowTaint: true,
      logging: false,
      scale: Math.min(1.5, window.devicePixelRatio || 1),
      ignoreElements: (element) => element.closest('[role="dialog"]') !== null,
    });

    const blob: Blob | null = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
    if (!blob) {
      return undefined;
    }

    const filename = `feedback-${new Date().toISOString().replace(/[:.]/g, '-')}.png`;
    return new File([blob], filename, { type: 'image/png' });
  } catch {
    return undefined;
  }
}
